import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";

/* === Helpers === */

function splitTopLevelTwo(expr) {
  let depth = 0;
  for (let i = 0; i < expr.length; i++) {
    const ch = expr[i];
    if (ch === "(") depth++;
    else if (ch === ")") depth = Math.max(0, depth - 1);
    else if ((ch === "+" || ch === "-" || ch === "−") && depth === 0) {
      const op = (ch === "-" || ch === "−") ? "-" : "+";
      const left = expr.slice(0, i).trim();
      const right = expr.slice(i + 1).trim();
      return [left, op, right];
    }
  }
  return [expr.trim(), "+", ""];
}

function toTerms(expr, v) {
  return expr
    .replace(/−/g, "-")
    .replace(/\s+/g, "")
    .replace(/\*/g, "")
    .replace(/-/g, "+-")
    .toLowerCase()
    .split("+")
    .filter(Boolean)
    .map((t) => {
      const m = t.match(/^([+-]?\d*)([a-z](?:\^\d+)?)?$/i);
      if (!m) return { coef: 0, pow: 0 };
      const coef = Number(
        m[1] === "" || m[1] === "+" || m[1] === "-"
          ? (t.startsWith("-") ? -1 : 1)
          : m[1]
      );
      if (!m[2]) return { coef, pow: 0 };
      const pow = m[2].includes("^") ? Number(m[2].split("^")[1]) : 1;
      return { coef, pow };
    });
}

function formatTerms(list, v) {
  const parts = [];
  for (const { coef, pow } of list) {
    if (coef === 0) continue;
    const sign = coef >= 0 ? "+" : "-";
    const abs = Math.abs(coef);
    let vp = "";
    if (pow === 1) vp = v;
    else if (pow > 1) vp = `${v}^${pow}`;
    const body = pow === 0 ? `${abs}` : (abs === 1 ? `${vp}` : `${abs}${vp}`);
    parts.push(`${sign} ${body}`);
  }
  return parts
    .join(" ")
    .replace(/^\+ /, "")
    .replace(/\s\+\s/g, " + ")
    .replace(/\s\-\s/g, " - ");
}

function reduceAndFormat(terms, v) {
  const map = new Map();
  for (const { coef, pow } of terms) map.set(pow, (map.get(pow) || 0) + coef);
  const reduced = Array.from(map.entries())
    .map(([pow, coef]) => ({ pow, coef }))
    .filter(({ coef }) => coef !== 0)
    .sort((a, b) => b.pow - a.pow);
  return formatTerms(reduced, v);
}

function normalizeExpression(expr, v) {
  const list = toTerms(expr, v);
  const map = new Map();
  for (const { coef, pow } of list) map.set(pow, (map.get(pow) || 0) + coef);
  return Array.from(map.entries())
    .sort((a, b) => b[0] - a[0])
    .map(([pow, coef], i) => {
      const sign = coef >= 0 ? (i === 0 ? "" : "+") : "-";
      const abs = Math.abs(coef);
      const vp = pow === 0 ? "" : (pow === 1 ? v : `${v}^${pow}`);
      const body = pow === 0 ? `${abs}` : (abs === 1 ? `${vp}` : `${abs}${vp}`);
      return `${sign}${body}`;
    })
    .join("");
}

function coefVar(a, v) { return a === 1 ? `${v}` : `${a}${v}`; }
function coefVarPow(a, v, p) { return p === 1 ? coefVar(a, v) : (a === 1 ? `${v}^${p}` : `${a}${v}^${p}`); }
function rand1to9() { return Math.floor(Math.random() * 9) + 1; }

/* === Local generators === */

function genBinomTimesBinom(v) {
  const a = rand1to9(), b = rand1to9(), c = rand1to9(), d = rand1to9();
  const sign1 = Math.random() < 0.5 ? "+" : "-";
  const sign2 = Math.random() < 0.5 ? "+" : "-";
  const bS = sign1 === "+" ? b : -b;
  const dS = sign2 === "+" ? d : -d;

  const expression = `(${coefVar(a, v)} ${sign1} ${b})(${coefVar(c, v)} ${sign2} ${d})`;

  const rawTerms = [
    { coef: a * c, pow: 2 },
    { coef: a * dS, pow: 1 },
    { coef: bS * c, pow: 1 },
    { coef: bS * dS, pow: 0 },
  ];
  const raw = formatTerms(rawTerms, v);

  const reducedTerms = [
    { coef: a * c, pow: 2 },
    { coef: a * dS + bS * c, pow: 1 },
    { coef: bS * dS, pow: 0 },
  ];
  const reduced = formatTerms(reducedTerms, v);

  return { expression, raw, reduced, rawTerms: rawTerms, reducedTerms };
}

function genBinomTimesTrinom(v) {
  const a = rand1to9(), b = rand1to9();
  const t = rand1to9();
  const u = rand1to9();
  const w = rand1to9();

  const expression = `(${coefVar(a, v)} + ${b})(${coefVarPow(t, v, 2)} + ${coefVar(u, v)} + ${w})`;

  const rawTerms = [
    { coef: a * t, pow: 3 },
    { coef: a * u, pow: 2 },
    { coef: a * w, pow: 1 },
    { coef: b * t, pow: 2 },
    { coef: b * u, pow: 1 },
    { coef: b * w, pow: 0 },
  ];
  const raw = formatTerms(rawTerms, v);
  const reduced = reduceAndFormat(rawTerms, v);

  return { expression, raw, reduced, rawTerms };
}

/* === GENERATE TASK === */

export function generateTask() {
  const variable = ["x", "y", "z"][Math.floor(Math.random() * 3)];

  const left = genBinomTimesBinom(variable);
  const right = genBinomTimesTrinom(variable);

  const sign = Math.random() < 0.5 ? "+" : "-";

  const expression = `${left.expression} ${sign} ${right.expression}`;

  const leftTerms = left.reducedTerms;
  const rightReducedTerms = toTerms(right.reduced, variable);
  const summed = [
    ...leftTerms,
    ...(sign === "+" ? rightReducedTerms : rightReducedTerms.map(({ coef, pow }) => ({ coef: -coef, pow }))),
  ];
  const expected = reduceAndFormat(summed, variable);

  return {
    type: "AddOrSubtractTrinomialTask",
    data: {
      expression,
      expected,
      sign,
      variable,
      leftRaw: left.raw,
      rightRaw: right.raw,
      leftExpanded: left.reduced,
      rightExpanded: right.reduced,
    }
  };
}

/* === RENDER TASK === */

export function renderTask({ data }) {
  return (
    <div className="cmd-container">
      <style>{`
        .cmd-container { margin-bottom: 14px; }
        .cmd-wide { display: inline; }
        .cmd-narrow { display: none; }
        @media (max-width: 880px) {
          .cmd-container { margin-bottom: 18px; }
          .cmd-wide { display: none; }
          .cmd-narrow { display: block; }
        }
      `}</style>

      <p className="cmd-wide">
        Uprość wyrażenie: <InlineMath math={data.expression} />
      </p>

      <div className="cmd-narrow">
        <p>Uprość wyrażenie:</p>
        <BlockMath math={data.expression} />
      </div>
    </div>
  );
}

/* === INPUT === */

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <EquationInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      placeholder="np. 2x^3 + 7x^2 + 5x + 4"
    />
  );
}

/* === CHECK ANSWER === */

export function checkAnswer({ data }, userAnswer) {
  const v = data.variable || "x";
  if (!userAnswer || typeof userAnswer !== "string") {
    return { isCorrect: false, expected: data.expected };
  }
  return {
    isCorrect: normalizeExpression(userAnswer, v) === normalizeExpression(data.expected, v),
    expected: data.expected
  };
}

/* === ANSWER === */

export function renderAnswer({ data }) {
  return (
    <>
      <style>{`
        .ans-wide { display: inline; }
        .ans-narrow { display: none; }
        @media (max-width: 880px) {
          .ans-wide { display: none; }
          .ans-narrow { display: block; }
        }
      `}</style>

      <span className="ans-wide">
        <InlineMath math={data.expected} />
      </span>
      <span className="ans-narrow">
        <BlockMath math={data.expected} />
      </span>
    </>
  );
}

/* === USER ANSWER === */

export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  if (!raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={raw} />;
}

/* === EXPLANATION === */

export function renderExplanation({ data }) {
  const { expression, expected, leftExpanded, rightExpanded, leftRaw, rightRaw, sign, variable } = data;
  const v = variable || "x";
  const [leftExpr, op, rightExpr] = splitTopLevelTwo(expression);

  const negRightText = formatTerms(
    toTerms(rightExpanded, v).map(({ coef, pow }) => ({ coef: -coef, pow })),
    v
  );

  const splitSegments = (sum) => {
    const s = ("+" + sum.replace(/−/g, "-")).replace(/\s+/g, " ");
    const m = s.match(/([+\-]\s*[^+\-]+)/g) || [s];
    return m.map(t => t.trim());
  };
  const packLines = (segments, maxLen = 30) => { // było 24 -> 30
    const lines = [];
    let cur = "";
    for (const seg of segments) {
      if (cur.length === 0) {
        cur = seg;
      } else if ((cur + " " + seg).length <= maxLen) {
        cur += " " + seg;
      } else {
        lines.push(cur);
        cur = seg;
      }
    }
    if (cur) lines.push(cur);
    return lines;
  };

  /* ---------- DESKTOP ---------- */
  const stepWide =
    sign === "+"
      ? String.raw`
\begin{array}{c}
${leftExpr} \; + \; ${rightExpr} \; = \\[8pt]
=\; ${leftRaw} \; + \; ${rightRaw} \; = \\[8pt]
=\; ${leftExpanded} \; + \; ${rightExpanded}
\end{array}
`
      : (() => {
          const rf = negRightText;
          const joiner = rf.trim().startsWith("-") ? String.raw`\;` : String.raw`\; + \;`;
          return String.raw`
\begin{array}{c}
${leftExpr} \; - \; ${rightExpr} \; = \\[8pt]
=\; ${leftRaw} \; - \; (${rightRaw}) \; = \\[8pt]
=\; ${leftExpanded} \; - \; (${rightExpanded}) \; = \\[8pt]
=\; ${leftExpanded} ${joiner} ${rf}
\end{array}
`;
        })();

  /* ---------- MOBILE ---------- */
  const rightRawSegs = splitSegments(rightRaw);
  const rightRawLines = packLines(rightRawSegs, 30);
  if (rightRawLines.length > 0) rightRawLines[0] = rightRawLines[0].replace(/^\+\s*/, "");

  const rightExpSegs = splitSegments(rightExpanded);
  const rightExpLines = packLines(rightExpSegs, 30);
  if (rightExpLines.length > 0) rightExpLines[0] = rightExpLines[0].replace(/^\+\s*/, "");

  const negSegs = splitSegments(negRightText);
  const negLines = packLines(negSegs, 30);
  if (negLines.length > 0) negLines[0] = negLines[0].replace(/^\+\s*/, "");

  const stepNarrow =
    sign === "+"
      ? String.raw`
\begin{array}{c}
${leftExpr} \; + \\[10pt]
+\; ${rightExpr} \; = \\[10pt]
=\; ${leftRaw} \; + \\[10pt]
+\; ${rightRaw} \; = \\[10pt]
=\; ${leftExpanded} \; + \\[10pt]
+\; ${rightExpanded}
\end{array}
`
      : (() => {
          // UWAGA: tutaj stosujemy \\; (backslash-escaped), żeby uniknąć „;” w renderze
          const linesRaw = [
            `-\\; (${rightRawLines[0]}${rightRawLines.length > 1 ? " \\, +" : ""})`
          ];
          for (let i = 1; i < rightRawLines.length; i++) {
            const isLast = i === rightRawLines.length - 1;
            linesRaw.push(`+\\; ${rightRawLines[i]}${isLast ? ")" : " \\, +"}`);
          }

          const linesExp = [
            `-\\; (${rightExpLines[0]}${rightExpLines.length > 1 ? " \\, +" : ""})`
          ];
          for (let i = 1; i < rightExpLines.length; i++) {
            const isLast = i === rightExpLines.length - 1;
            linesExp.push(`+\\; ${rightExpLines[i]}${isLast ? ")" : " \\, +"}`);
          }

          const endLines = [
            `=\\; ${leftExpanded} \\; +`
          ];
          for (let i = 0; i < negLines.length; i++) {
            endLines.push(`${negLines[i]}`);
          }

          return String.raw`
\begin{array}{c}
${leftExpr} \; - \\[10pt]
-\\; ${rightExpr} \; = \\[10pt]
=\\; ${leftRaw} \\; - \\[10pt]
${linesRaw.join(" \\\\[10pt]\n")} \\[10pt]
=\\; ${leftExpanded} \\; - \\[10pt]
${linesExp.join(" \\\\[10pt]\n")} \\[10pt]
${endLines.join(" \\\\[10pt]\n")}
\end{array}
`;
        })();

  return (
    <div className="exp-wrap" style={{ lineHeight: 1.9 }}>
      <style>{`
        .exp-wrap p { text-align: left; }
        .exp-wrap .katex-display { text-align: center !important; margin: 12px 0 14px !important; }
        .exp-wide { display: block; }
        .exp-narrow { display: none; }
        @media (max-width: 880px) {
          .exp-wide { display: none; }
          .exp-narrow { display: block; }
          .exp-narrow .katex-display { margin-left: -10px !important; }
        }
      `}</style>

      <p><strong>Krok 1:</strong> Wymnóż nawiasy i uprość w każdym iloczynie:</p>

      <div className="exp-wide">
        <BlockMath math={stepWide} />
      </div>

      <div className="exp-narrow">
        <BlockMath math={stepNarrow} />
      </div>

      <p><strong>Krok 2:</strong> Zredukuj wyrazy podobne (porządek malejących potęg):</p>
      <BlockMath math={expected} />
    </div>
  );
}
